import {
  CalendarXIcon,
  ClipboardListIcon,
  CloudCogIcon,
  GaugeIcon,
  HourglassIcon,
  LayoutTemplateIcon,
  LibraryIcon,
  MicIcon,
  MonitorCogIcon,
  RadarIcon,
  ScaleIcon,
  ShieldCheckIcon,
  SlidersIcon,
  TargetIcon,
  TimerResetIcon,
  WavesIcon,
  ZapIcon,
} from 'lucide-react'

type LucideIcon = typeof ZapIcon

export interface IconItem {
  icon: LucideIcon
  title: string
  description: string
}

/** The "Sunday Night" problem, broken into what it actually costs the instructor. */
export const painPoints: IconItem[] = [
  {
    icon: CalendarXIcon,
    title: 'Weekends Gone',
    description:
      'Slide decks and lab manuals get built on Saturday night because there is no room for them Monday to Friday.',
  },
  {
    icon: HourglassIcon,
    title: 'Two Full-Time Jobs',
    description:
      'You are expected to master industrial physics and automation, and to be a full-time curriculum designer.',
  },
  {
    icon: LibraryIcon,
    title: 'Outdated Textbooks',
    description:
      'The industry moves faster than publishing. Books are behind the plant floor before they are printed.',
  },
  {
    icon: ClipboardListIcon,
    title: 'Rubrics From Scratch',
    description:
      'Writing high-quality assessment rubrics and exams takes hours you simply do not have this semester.',
  },
  {
    icon: MicIcon,
    title: 'Explaining The Hard Parts',
    description:
      'PID, cascade control and 4-20mA scaling need the right analogy on the first pass, not the third.',
  },
  {
    icon: ShieldCheckIcon,
    title: 'Lab Safety Paperwork',
    description:
      'Every practical session needs protocols that hold up to an inspection, written and re-written by you.',
  },
]

export interface Standard {
  code: string
  label: string
}

export const standards: Standard[] = [
  { code: 'ANSI', label: 'National standards alignment' },
  { code: 'ISA-5.1', label: 'Instrumentation symbology' },
  { code: 'IEEE', label: 'Engineering practice' },
  { code: 'OSHA', label: 'Lab safety requirements' },
]

export interface Volume {
  icon: LucideIcon
  number: number
  title: string
  focus: string
}

export const volumes: Volume[] = [
  {
    icon: WavesIcon,
    number: 1,
    title: 'The DNA of Measurement',
    focus: 'The physics of sensing and 4-20mA mastery.',
  },
  {
    icon: GaugeIcon,
    number: 2,
    title: 'Elite Sensors',
    focus: 'In-depth guides for Pressure, Flow, Level and Temp.',
  },
  {
    icon: SlidersIcon,
    number: 3,
    title: 'Control & Strategy',
    focus: 'PID tuning, cascade control and feedforward logic.',
  },
  {
    icon: LayoutTemplateIcon,
    number: 4,
    title: 'The Plant Map',
    focus: 'P&ID mastery and ISA-5.1 symbology.',
  },
  {
    icon: CloudCogIcon,
    number: 5,
    title: 'Instrumentation 4.0',
    focus: 'IIoT, Edge Computing and Digital Twins.',
  },
  {
    icon: MonitorCogIcon,
    number: 6,
    title: 'Management & Planning',
    focus: 'Curriculum mapping and lab organization.',
  },
]

export interface Bonus {
  number: number
  title: string
  description: string
  /** Individual price if sold on its own, in USD. Sums to BONUS_VALUE. */
  value: number
  image: string
}

export const bonuses: Bonus[] = [
  {
    number: 1,
    title: 'The Master Lecture Scripts',
    description: 'Word-for-word teaching guides for the topics students always get stuck on.',
    value: 14,
    image: 'https://cdn.magicpatterns.com/uploads/4NnPLSNT5cRukyzhHcPU8m/bo1.webp',
  },
  {
    number: 2,
    title: 'The Technical Encyclopedia',
    description: '350+ instrumentation terms explained in language your students actually use.',
    value: 12,
    image: 'https://cdn.magicpatterns.com/uploads/ncmEQvJt4uWAuJrNcHQn9x/bo2.webp',
  },
  {
    number: 3,
    title: 'The Simple Explanations Bank',
    description: 'Powerful analogies that make abstract control concepts finally click.',
    value: 9,
    image: 'https://cdn.magicpatterns.com/uploads/3u38cNHZBkXzcowso12PLa/bo3.webp',
  },
  {
    number: 4,
    title: 'Lab Safety Checklists',
    description: 'Professional, OSHA-ready protocols you can print and use in the next session.',
    value: 7,
    image: 'https://cdn.magicpatterns.com/uploads/2D2AvQxLkDb7YWQGjg1TgP/bo4.webp',
  },
  {
    number: 5,
    title: 'Industrial Case Studies',
    description: 'Real-world plant failures, packaged for group discussion and root-cause work.',
    value: 9,
    image: 'https://cdn.magicpatterns.com/uploads/ksUirsxQYcYKKqoNNvYReo/bo5.webp',
  },
  {
    number: 6,
    title: 'Assessment & Rubric Bank',
    description: '100+ exam questions plus grading tools that cut marking time down to minutes.',
    value: 9,
    image: 'https://cdn.magicpatterns.com/uploads/u7oWvwWHAn3U5HuZT9DAFA/bo6.webp',
  },
]

/** Single source of truth for every price shown on the page. */
export const CORE_PRICE = 96
export const BONUS_VALUE = bonuses.reduce((total, bonus) => total + bonus.value, 0)
export const TOTAL_VALUE = CORE_PRICE + BONUS_VALUE
export const SALE_PRICE = 27
export const DISCOUNT_PERCENT = Math.round((1 - SALE_PRICE / TOTAL_VALUE) * 100)

export const advantages: IconItem[] = [
  {
    icon: ScaleIcon,
    title: 'Industry-Standard Aligned',
    description: 'Written against ISA, ANSI, IEEE and OSHA requirements, not generic theory.',
  },
  {
    icon: TimerResetIcon,
    title: 'Time Saver',
    description: 'Cut your lesson planning time by up to 80% with ready-to-teach material.',
  },
  {
    icon: RadarIcon,
    title: 'Modern Content',
    description: 'Industry 4.0, wireless mesh networks, smart sensors and edge diagnostics.',
  },
  {
    icon: TargetIcon,
    title: 'Pedagogical Precision',
    description: 'Built to improve student retention, lab success and first-try certification rates.',
  },
]

export const offerIncludes: string[] = [
  'Vol 1: The DNA of Measurement',
  'Vol 2: Elite Sensors',
  'Vol 3: Control & Strategy',
  'Vol 4: The Plant Map',
  'Vol 5: Instrumentation 4.0',
  'Vol 6: Management & Planning',
  'All 6 Instructor Advantage bonuses ($60 value)',
  'One-time payment. Lifetime access.',
]

export interface Testimonial {
  quote: string
  initials: string
  name: string
  role: string
}

/** One real instructor quote, given the spotlight instead of padded with filler. */
export const featuredTestimonial: Testimonial = {
  quote:
    'The IIIS 2026 changed my Sunday nights. I no longer worry about what I am going to teach. The scripts are gold, and my students are finally passing their certification exams on the first try.',
  initials: 'DM',
  name: 'David M.',
  role: 'Trade School Instructor',
}

export interface FaqItem {
  question: string
  answer: string
}

export const faqs: FaqItem[] = [
  {
    question: 'Is this a physical book?',
    answer:
      'No. It is a digital system (PDF and Markdown), so you can copy-paste the lecture scripts and print the rubrics immediately.',
  },
  {
    question: 'Does it cover PLC programming?',
    answer:
      'It focuses on the instrumentation side (inputs and outputs) and control strategy, which is the foundation every PLC program is built on.',
  },
  {
    question: 'Is it updated for 2026?',
    answer:
      'Yes, including WirelessHART, ISA100 and edge diagnostics, alongside the classic measurement and control fundamentals.',
  },
]

export const ecosystemStats: { value: string; label: string }[] = [
  { value: '6', label: 'Core volumes' },
  { value: '6', label: 'Instructor bonuses' },
  { value: '350+', label: 'Terms defined' },
  { value: '100+', label: 'Exam questions' },
]

export const HERO_IMAGE =
  'https://cdn.magicpatterns.com/uploads/tgoaCL2tGFwMEA7xebvevW/turr.webp'
